import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Link2, Plus, BookOpen, Upload, Trash2, FileText } from "lucide-react";

const FEATURED = [
  { title: "Khan Academy Math (7th–8th grade)", url: "https://www.khanacademy.org/math/pre-algebra", subject: "math", kind: "game", note: "Free interactive lessons and practice." },
  { title: "Math Playground", url: "https://www.mathplayground.com/", subject: "math", kind: "game", note: "Math games and logic puzzles." },
  { title: "CommonLit (Reading)", url: "https://www.commonlit.org/", subject: "reading-writing", kind: "worksheet", note: "Free leveled passages with questions." },
  { title: "SmithsonianOcean", url: "https://ocean.si.edu/", subject: "oceanography", kind: "link", note: "Deep-dive articles on marine life." },
  { title: "Duolingo (Spanish)", url: "https://www.duolingo.com/course/es/en/Learn-Spanish", subject: "spanish", kind: "game", note: "Bite-size Spanish practice." },
  { title: "Scratch", url: "https://scratch.mit.edu/", subject: "creative-tech-art", kind: "game", note: "Build stories, games, and animations." },
];

export default function Library() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ title: "", url: "", subject: "general", kind: "link", note: "" });
  const [file, setFile] = useState(null);

  const load = async () => { const { data } = await api.get("/resources"); setItems(data); };
  useEffect(() => { load(); }, []);

  const readAsDataUrl = (f) => new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(f); });

  const add = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form };
      if (file) {
        if (file.size > 4 * 1024 * 1024) return toast.error("Please keep files under 4 MB");
        payload.file_data = await readAsDataUrl(file);
        payload.file_name = file.name;
        payload.kind = file.type.startsWith("image/") ? "image" : (file.type.includes("pdf") ? "pdf" : payload.kind);
      }
      await api.post("/resources", payload);
      toast.success("Resource added");
      setForm({ title: "", url: "", subject: "general", kind: "link", note: "" });
      setFile(null);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const addFeatured = async (r) => {
    try { await api.post("/resources", r); toast.success(`Added ${r.title}`); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const remove = async (id) => {
    if (!confirm("Remove this resource?")) return;
    try { await api.delete(`/resources/${id}`); load(); toast.success("Removed"); }
    catch { toast.error("Could not remove"); }
  };

  return (
    <div className="space-y-8" data-testid="library-page">
      <div>
        <div className="chip mb-2">Resource library</div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900">Books, videos, worksheets & links</h1>
        <p className="text-slate-600 mt-2">Anything your parent-teacher shares lives here.</p>
      </div>

      {user?.role === "parent" && (
        <>
          <section className="p-6 rounded-3xl bg-white border border-slate-200">
            <div className="flex items-center gap-2 mb-3"><Plus size={18} className="text-teal-700" /><h2 className="font-display font-semibold text-lg">Add resource</h2></div>
            <form onSubmit={add} className="grid sm:grid-cols-2 gap-3">
              <input data-testid="res-title" required placeholder="Title (e.g., ‘Fractions practice’)" value={form.title} onChange={(e)=>setForm({...form, title: e.target.value})} className="px-4 py-3 rounded-xl border border-slate-200" />
              <input data-testid="res-url" type="url" placeholder="URL (https://…) — or attach a file below" value={form.url} onChange={(e)=>setForm({...form, url: e.target.value})} className="px-4 py-3 rounded-xl border border-slate-200" />
              <select data-testid="res-subject" value={form.subject} onChange={(e)=>setForm({...form, subject: e.target.value})} className="px-4 py-3 rounded-xl border border-slate-200 bg-white">
                {["general","math","reading-writing","oceanography","history","spanish","creative-tech-art"].map(s => <option key={s}>{s}</option>)}
              </select>
              <select data-testid="res-kind" value={form.kind} onChange={(e)=>setForm({...form, kind: e.target.value})} className="px-4 py-3 rounded-xl border border-slate-200 bg-white">
                {["link","video","worksheet","book","game","quiz","image","pdf"].map(k => <option key={k}>{k}</option>)}
              </select>
              <label className="sm:col-span-2 px-4 py-3 rounded-xl border border-dashed border-slate-300 bg-slate-50 flex items-center gap-2 cursor-pointer">
                <Upload size={16} className="text-slate-500" />
                <span className="text-sm text-slate-600">{file ? file.name : "Or upload a file / image (under 4 MB)"}</span>
                <input data-testid="res-file" type="file" accept="image/*,application/pdf,.doc,.docx,.txt" onChange={(e)=>setFile(e.target.files?.[0] || null)} className="hidden" />
              </label>
              <textarea data-testid="res-note" placeholder="Note (optional)" value={form.note} onChange={(e)=>setForm({...form, note: e.target.value})} className="sm:col-span-2 px-4 py-3 rounded-xl border border-slate-200" rows={2} />
              <button data-testid="res-submit" className="sm:col-span-2 px-6 py-3 rounded-full bg-teal-700 hover:bg-teal-800 text-white font-semibold transition-colors">Add resource</button>
            </form>
          </section>

          <section>
            <h2 className="font-display text-lg font-semibold text-slate-900 mb-2">Featured resources · one-tap add</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="featured-resources">
              {FEATURED.map((r) => (
                <button key={r.url} onClick={()=>addFeatured(r)} data-testid={`featured-${r.subject}`}
                  className="p-4 rounded-2xl bg-white border border-slate-200 hover:border-teal-300 text-left">
                  <div className="chip">{r.subject}</div>
                  <div className="font-semibold text-slate-900 mt-2">{r.title}</div>
                  <div className="text-xs text-slate-500 mt-1">{r.note}</div>
                </button>
              ))}
            </div>
          </section>
        </>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="resource-grid">
        {items.map((r) => {
          const href = r.url || r.file_data;
          const isFile = !!r.file_data;
          return (
            <div key={r.id} data-testid={`resource-${r.id}`}
              className="p-5 rounded-3xl bg-white border border-slate-200 hover:border-teal-300 hover:shadow-sm transition-all">
              <div className="flex items-center justify-between">
                <div className="text-xs text-slate-500 uppercase tracking-widest flex items-center gap-1"><BookOpen size={12} /> {r.subject} · {r.kind}</div>
                {user?.role === "parent" && r.added_by === user.id && (
                  <button data-testid={`res-remove-${r.id}`} onClick={()=>remove(r.id)} className="text-slate-400 hover:text-red-500"><Trash2 size={14} /></button>
                )}
              </div>
              <div className="mt-2 font-display text-lg font-semibold text-slate-900">{r.title}</div>
              {r.kind === "image" && r.file_data && <img src={r.file_data} alt="" className="mt-2 rounded-xl w-full aspect-video object-cover" />}
              {href ? (
                <a href={href} download={isFile ? (r.file_name || r.title) : undefined} target={isFile ? undefined : "_blank"} rel="noreferrer"
                  className="mt-2 text-sm text-teal-700 font-semibold truncate flex items-center gap-1">
                  {isFile ? <><FileText size={12} /> {r.file_name || "Download"}</> : <><Link2 size={12} /> {r.url}</>}
                </a>
              ) : (
                <div className="mt-2 text-xs text-slate-400">No link or file attached.</div>
              )}
              {r.note && <div className="mt-2 text-sm text-slate-600">{r.note}</div>}
            </div>
          );
        })}
        {items.length === 0 && <div className="text-slate-500 text-sm">No resources yet.</div>}
      </div>
    </div>
  );
}
